/**
 * AI workflows are units of behavior the WhatsApp bot can perform.
 * Built-in workflows (business info / book / reschedule / cancel) have hardcoded tool sets and
 * can be toggled on/off. Custom workflows are authored by the business owner
 * with a natural-language trigger, instructions, and definition-of-done.
 */
export enum WorkflowTypeEnum {
  /** Answer questions about the business itself — services, prices, hours, location. */
  BusinessInfo = 'business_info',
  Book = 'book',
  Reschedule = 'reschedule',
  Cancel = 'cancel',
  Custom = 'custom',
}

/**
 * Tools that can be granted to a custom workflow. Built-in workflows have a
 * fixed tool set and do not use this list.
 */
export enum CustomWorkflowToolEnum {
  /** Public business info: services, prices, hours, providers. */
  GetBusinessInfo = 'getBusinessInfo',
  /** Ask the customer for one or more photos with guidance text. */
  RequestPhotos = 'requestPhotos',
  /** Fuzzy-match a free-text customer description to the closest service for quoting. */
  FindClosestService = 'findClosestService',
  /** Ping the business owner with a summary so they can follow up. */
  NotifyOwner = 'notifyOwner',
  /** Hand off the conversation to a human and pause the bot until the end of the day. */
  HandoffToHuman = 'handoffToHuman',
}

export interface IAiWorkflow {
  id?: string;
  bid?: string;
  type: WorkflowTypeEnum;
  /** Whether this workflow is active in the bot's prompt. */
  enabled: boolean;
  /** Display name shown to the business owner. */
  name: string;
  /** Short description shown on the card. */
  description?: string;
  /** Natural-language description of when the AI should enter this workflow. */
  whenToTrigger: string;
  /** Natural-language instructions for HOW the AI should handle the task. */
  instructions: string;
  /** Natural-language description of when the workflow is complete. */
  definitionOfDone: string;
  /** Tools the AI can call inside this workflow. Custom workflows only. */
  enabledTools?: CustomWorkflowToolEnum[];
  /** When DOD is met, send the owner a structured summary push + WhatsApp echo. */
  notifyOwnerOnDone?: boolean;
  /** Sort order within the list (custom workflows are draggable). */
  sortOrder?: number;
  createdAt?: Date | string;
  updatedAt?: Date | string;
}

/**
 * Metadata used by the dashboard to render built-in workflow cards.
 * Custom workflows render from `IAiWorkflow` directly.
 */
export interface IBuiltInWorkflowMeta {
  type: WorkflowTypeEnum;
  name: string;
  description: string;
  icon: string;
  color: string;
  /** Default whenToTrigger / instructions / DOD seeded the first time the workflow is edited. */
  defaultWhenToTrigger: string;
  defaultInstructions: string;
  defaultDefinitionOfDone: string;
  /** Tool names this built-in uses (informational — backend has the real list). */
  toolsUsed: string[];
}

/** Metadata for a custom-workflow tool, used in the UI's tool-picker. */
export interface ICustomWorkflowToolMeta {
  tool: CustomWorkflowToolEnum;
  name: string;
  description: string;
  icon: string;
}
